import React, { FC, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
	Orientation,
	Platform,
	DeviceType,
	AppView,
	Lightbox,
	SceneItemData,
	RangeSettings,
	DragDisabledItem,
	IModuleSwiperItem,
	GhostPositionHelper,
	ScrollDistance,
	WriteDirection,
	PopOver,
} from 'sofa-reborn-react';
import ModuleSwiper from '../components/ModuleSwiper';
import { ApplicationUtils } from '../util/ApplicationUtils';
import { setDragItemId, setSelectedItemId, setSwiperEventType } from '../store/swiper';
import { RootState, AppDispatch } from '../store';
import { ProductData } from '@ikea-aoa/component-view-3d';
import { setHasFirstInteractionHappened } from '../store/application/actions';
import {
	showPopOver,
	hidePopOver,
	buildTipHasBeenShown,
	hideBuildTip,
} from '../store/popOver/actions';
import { setModuleSwiperItems } from '../store/application/thunks';
import { setKeepModule } from '../store/scene';
import { swipeInModuleSwiper } from '../store/application/actions';

const ModuleSwiperContainer: FC = () => {
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const orientation = useSelector<RootState, Orientation>((state) => state.deviceInfo.orientation);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const moduleItems = useSelector<RootState, IModuleSwiperItem[]>(
		(state) => state.swiper.moduleItems
	);
	const appView = useSelector<RootState, AppView>((state) => state.application.appView);
	const currentLightbox = useSelector<RootState, Lightbox>(
		(state) => state.application.currentLightbox
	);
	const sceneItems = useSelector<RootState, SceneItemData[]>((state) => state.scene.sceneItems);
	const products = useSelector<RootState, ProductData>((state) => state.products.products);
	const rangeSettings = useSelector<RootState, RangeSettings>(
		(state) => state.settings.rangeSettings as RangeSettings
	);
	const storeId = useSelector<RootState, string>((state) => state.localization.storeId as string);
	const dispatch = useDispatch<AppDispatch>();
	const dragItemId = useSelector<RootState, string>((state) => state.swiper.dragItemId as string);
	const showPlacementInformation = useSelector<RootState, PopOver>(
		(state) => state.popOver.placementInformation
	);

	const [ghostHelper, setHelperPosition] = useState<GhostPositionHelper>({
		imgSrc: '',
		x: 0,
		y: 0,
		z: -1,
	});

	const [selectedIndex, setSelectedIndex] = useState<number>(0);
	const [slideIndex, setSlideIndex] = useState<number>(0);
	const [scrollDistance, setScrollDistance] = useState<ScrollDistance>({
		top: 0,
		bot: 0,
		lef: 0,
		rig: 0,
	});

	const isScrollable = (element: HTMLElement | null): boolean => {
		if (element && deviceType === 'tablet') {
			return element.scrollWidth > element.clientWidth;
		}

		if (element) {
			return element.scrollHeight > element.clientHeight;
		}

		return false;
	};

	const parseModuleSwiperItems = (
		moduleItems: IModuleSwiperItem[],
		deviceType: DeviceType
	): IModuleSwiperItem[] => {
		moduleItems.forEach(function (moduleItem) {
			if (deviceType === 'tablet') {
				if (moduleItem.productName.length > 12) {
					moduleItem.productName = ApplicationUtils.cutLongText(moduleItem.productName, 12);
				}
			}
		});

		return moduleItems;
	};

	const moduleLimitReached = (
		sceneItems: SceneItemData[],
		products: ProductData,
		deviceType: DeviceType,
		rangeSettings: RangeSettings
	): boolean => {
		let nrOfSeats = 0;

		sceneItems.forEach((sceneObject) => {
			const sceneObjectFamily = products[sceneObject.articleId].filter.family;
			/* Armrests, headrests and legs are not considered as seat modules. */
			if (
				sceneObjectFamily !== 'ARMREST' &&
				sceneObjectFamily !== 'HEADREST' &&
				sceneObjectFamily !== 'BACKREST' &&
				sceneObjectFamily !== 'CUSHION' &&
				sceneObject.articleId !== 'leg'
			) {
				nrOfSeats++;
			}
		});

		return deviceType === 'mobile'
			? nrOfSeats >= rangeSettings.moduleLimitMobile
			: nrOfSeats >= rangeSettings.moduleLimit;
	};

	const onItemDragStart = (
		dragStartItemId: string,
		appView: AppView,
		deviceType: DeviceType,
		currentLightbox: Lightbox,
		sceneItems: SceneItemData[],
		products: ProductData,
		rangeSettings: RangeSettings
	): void => {
		if (appView === 'build-view' && currentLightbox === 'none') {
			const dragObjectFamily = products[dragStartItemId].filter.family;
			const checkModuleLimit =
				dragObjectFamily === 'ARMREST' ||
				dragObjectFamily === 'HEADREST' ||
				dragObjectFamily === 'BACKREST' ||
				dragObjectFamily === 'CUSHION';
			if (
				!checkModuleLimit &&
				moduleLimitReached(sceneItems, products, deviceType, rangeSettings)
			) {
				dispatch(showPopOver({ type: 'build-limit' }));
			} else {
				dispatch(setSwiperEventType({ type: 'drag', productId: null }));
				dispatch(setDragItemId(dragStartItemId, 'swiper'));
				dispatch(hideBuildTip(true));
				setTimeout(() => {
					dispatch(buildTipHasBeenShown(true));
					dispatch(hideBuildTip(false));
				}, 3000);
				if (dragStartItemId !== null) {
					dispatch(setHasFirstInteractionHappened(true));
				}
				dispatch(
					hidePopOver({
						type: 'all',
					})
				);
				dispatch(setKeepModule(false));
			}
		}
	};

	const onItemDragDisabled = (
		ref: React.RefObject<HTMLDivElement>,
		dragDisabledItem: DragDisabledItem,
		orientation: Orientation,
		appView: AppView,
		currentLightbox: Lightbox
	): void => {
		if (ref.current) {
			if (appView === 'build-view' && currentLightbox === 'none') {
				dispatch(setSelectedItemId(null));
				const clientRect = ref.current.getBoundingClientRect();
				dragDisabledItem.position.top = clientRect.top;
				dragDisabledItem.position.left = clientRect.left;

				dispatch(hidePopOver({ type: 'disabled-drag' }));
				const top =
					orientation === 'portrait'
						? dragDisabledItem.position.top
						: dragDisabledItem.size.height / 2 + dragDisabledItem.position.top;
				const left =
					orientation === 'portrait'
						? dragDisabledItem.size.width / 2 + dragDisabledItem.position.left
						: dragDisabledItem.position.left;
				dispatch(showPopOver({ type: 'disabled-drag', top, left }));
			}
		}
	};

	const onItemSelect = (
		selectedItem: IModuleSwiperItem,
		appView: AppView,
		deviceType: DeviceType,
		currentLightbox: Lightbox,
		sceneItems: SceneItemData[],
		products: ProductData,
		rangeSettings: RangeSettings
	): void => {
		if (appView === 'build-view' && currentLightbox === 'none') {
			const dragObjectFamily = products[selectedItem.id].filter.family;
			const checkModuleLimit =
				dragObjectFamily === 'ARMREST' ||
				dragObjectFamily === 'HEADREST' ||
				dragObjectFamily === 'BACKREST' ||
				dragObjectFamily === 'CUSHION';
			if (
				!checkModuleLimit &&
				moduleLimitReached(sceneItems, products, deviceType, rangeSettings)
			) {
				dispatch(showPopOver({ type: 'build-limit' }));
			} else {
				dispatch(setSelectedItemId(selectedItem.id));
				dispatch(hideBuildTip(true));
				setTimeout(() => {
					dispatch(buildTipHasBeenShown(true));
					dispatch(hideBuildTip(false));
				}, 3000);
				if (selectedItem.id !== null) {
					dispatch(setHasFirstInteractionHappened(true));
				}
				if (showPlacementInformation.showPopOver) {
					dispatch(hidePopOver({ type: 'placement-information' }));
				}
				dispatch(
					hidePopOver({
						type: 'all',
					})
				);
			}
		}
	};

	const onGhostPositionUpdate = (ghost: GhostPositionHelper) => {
		setHelperPosition(ghost);
	};

	const checkScroll = (direction: string) => {
		const swiper = document.getElementById('moduleSwiper');

		if (swiper) {
			const containerHeightWidth =
				direction === 'horizontal' ? swiper?.clientWidth : swiper?.clientHeight;
			const scrollHeightWidth =
				direction === 'horizontal' ? swiper?.scrollWidth : swiper?.scrollHeight;
			const scroll = direction === 'horizontal' ? swiper?.scrollLeft : swiper?.scrollTop;
			const scrollPercentage = ((scroll + containerHeightWidth) / scrollHeightWidth) * 100;

			if (direction === 'horizontal') {
				setScrollDistance({ top: 0, bot: 0, lef: scroll, rig: scrollPercentage });
			} else {
				setScrollDistance({ top: scroll, bot: scrollPercentage, lef: 0, rig: 0 });
			}
		}
	};

	const updateScroll = (direction: string) => {
		const swiper = document.getElementById('moduleSwiper');

		if (swiper) {
			if (direction === 'up') {
				swiper.scroll({
					top: scrollDistance.top - 320 < 0 ? 0 : scrollDistance.top - 320,
					left: 0,
					behavior: 'smooth',
				});
			} else if (direction === 'down') {
				swiper.scroll({
					top:
						scrollDistance.top + 320 > swiper.scrollHeight
							? swiper.scrollHeight
							: scrollDistance.top + 320,
					left: 0,
					behavior: 'smooth',
				});
			} else if (direction === 'left') {
				swiper.scroll({
					left: swiper.scrollLeft - 320,
					top: 0,
					behavior: 'smooth',
				});
			} else if (direction === 'right') {
				swiper.scroll({
					left: swiper.scrollLeft + 320,
					top: 0,
					behavior: 'smooth',
				});
			}
		}
	};

	useEffect(() => {
		dispatch(setModuleSwiperItems());
	}, [storeId]);

	useEffect(() => {
		const ghost: GhostPositionHelper = {
			imgSrc: '',
			x: 0,
			y: 0,
			z: -1,
		};

		setHelperPosition(ghost);
	}, [dragItemId]);

	if (moduleItems.length === 0) {
		return null;
	}

	return (
		<ModuleSwiper
			platform={platform}
			orientation={orientation}
			writeDirection={writeDirection}
			deviceType={deviceType}
			dragItemId={dragItemId}
			moduleItems={parseModuleSwiperItems(moduleItems, deviceType)}
			ghostHelper={ghostHelper}
			selectedIndex={selectedIndex}
			scrollDistance={scrollDistance}
			twoColumns={!['mobile', 'tablet'].includes(deviceType)}
			slideIndex={slideIndex}
			isScrollable={isScrollable(document.getElementById('moduleSwiper'))}
			onItemDragStart={(dragItemId): void => {
				onItemDragStart(
					dragItemId,
					appView,
					deviceType,
					currentLightbox,
					sceneItems,
					products,
					rangeSettings
				);
			}}
			onGhostPositionUpdate={(ghost) => {
				onGhostPositionUpdate(ghost);
			}}
			onItemDragDisabled={(
				dragDisabledItem: DragDisabledItem,
				ref: React.RefObject<HTMLDivElement>
			): void => onItemDragDisabled(ref, dragDisabledItem, orientation, appView, currentLightbox)}
			onSwipe={(slideIndex): void => {
				setSlideIndex(slideIndex);
				dispatch(swipeInModuleSwiper());
			}}
			onItemSelect={(selectedItem: IModuleSwiperItem): void =>
				onItemSelect(
					selectedItem,
					appView,
					deviceType,
					currentLightbox,
					sceneItems,
					products,
					rangeSettings
				)
			}
			setSelectedIndex={(index) => {
				setSelectedIndex(index);
			}}
			checkScroll={(direction: string) => {
				checkScroll(direction);
			}}
			updateScroll={(direction: string): void => {
				updateScroll(direction);
			}}
		></ModuleSwiper>
	);
};

export default ModuleSwiperContainer;
